import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken, selectSessionId } from "../../sessionSlice";
import {
  gameAddPlayer,
  gameDeletePlayer,
  gameDiscussion,
  gameGetState,
  gameLeaderboard,
  gameRevealAnswer,
  gameRestartTimer,
  gameSplitTeams,
  gameStartQuestion,
  gameStrike,
  gameSubmitAnswer,
  gameUpdateState,
} from "../../functions/gameApi";
import { publishGameEvent } from "../../../lib/gameClientRtdb";

export default function GameHostPanel({ selectedQuestion, onStateChange }) {
  const token = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme } = useSelector((state) => state.theme);
  const [state, setState] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedOptionNo, setSelectedOptionNo] = useState("");
  const [timerLeft, setTimerLeft] = useState(null);
  const [, setUiTick] = useState(0);

  const selectedOptions = useMemo(() => {
    if (!selectedQuestion?.raw) return [];
    return selectedQuestion.optionsWithPoints || [];
  }, [selectedQuestion]);
  const isGameMode = (state?.mode || "game") === "game";
  const isCrowdMode = !isGameMode;
  const teamsReady = Number(state?.teams_created) === 1;
  const questionReleased = Number(state?.question_released) === 1;
  const hasSelectedPlayer = Boolean(selectedPlayerId);
  const hasValidSelectedOption = (() => {
    const n = Number(selectedOptionNo);
    return Number.isFinite(n) && n >= 1 && n <= 8;
  })();
  const selectedPlayer = players.find((p) => Number(p.id) === Number(selectedPlayerId));
  const leaderboardActive = Number(state?.leaderboard_visible) === 1;
  const discussionDeadline = Number(state?.discussion_deadline_ms);
  const discussionActive = Number.isFinite(discussionDeadline) && discussionDeadline > Date.now();

  const refresh = async () => {
    if (!token || !currentTheme) return;
    const data = await gameGetState({ token, currentTheme });
    if (data.success) {
      setState(data.state || null);
      setPlayers(data.players || []);
      setLeaderboard(data.leaderboard || []);
      if (typeof onStateChange === "function") {
        onStateChange(data.state || null);
      }
      if (data.state?.selected_player_id != null) {
        setSelectedPlayerId(Number(data.state.selected_player_id));
      }
      const sn = data.state?.selected_option_no;
      if (sn != null && Number(sn) >= 1 && Number(sn) <= 8) {
        setSelectedOptionNo(String(sn));
      } else {
        setSelectedOptionNo("");
      }
    }
  };

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [token, currentTheme]);

  useEffect(() => {
    if (selectedQuestion?.id != null) {
      refresh().catch(() => undefined);
    }
  }, [selectedQuestion?.id]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const deadline = Number(state?.question_deadline_ms);
      if (!Number.isFinite(deadline)) {
        setTimerLeft(null);
        return;
      }
      setTimerLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [state?.question_deadline_ms]);

  useEffect(() => {
    const id = window.setInterval(() => setUiTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const run = async (fn, okMessage, eventType, eventPayload = {}) => {
    try {
      await fn();
      if (eventType) {
        await publishGameEvent(sessionId, eventType, eventPayload);
      }
      await refresh();
      if (okMessage) Swal.fire("Success", okMessage, "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Request failed", "error");
    }
  };

  return (
    <div className="wml-panel-card">
      <div className="question-header">
        <div className="question-header-title">Host Controls</div>
      </div>
      <div className="wml-live-row">
        <div className="wml-live-left">
          <div className="game-mode-switch">
            <button
              type="button"
              className={`mode-btn ${(state?.mode || "game") === "game" ? "active" : ""}`}
              onClick={() =>
                run(
                  () =>
                    gameUpdateState({
                      token,
                      currentTheme,
                      patch: { mode: "game" },
                    }),
                  "Mode updated",
                  "MODE_CHANGED",
                  { mode: "game" }
                )
              }
            >
              Game Mode
            </button>
            <button
              type="button"
              className={`mode-btn ${(state?.mode || "game") === "crowd" ? "active" : ""}`}
              onClick={() =>
                run(
                  () =>
                    gameUpdateState({
                      token,
                      currentTheme,
                      patch: { mode: "crowd" },
                    }),
                  "Mode updated",
                  "MODE_CHANGED",
                  { mode: "crowd" }
                )
              }
            >
              Crowd Mode
            </button>
          </div>
          {isGameMode && !teamsReady ? (
            <>
              <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  placeholder="Player name"
                />
                <button
                  type="button"
                  className="btn outline"
                  onClick={() =>
                    run(
                      async () => {
                        await gameAddPlayer({
                          token,
                          currentTheme,
                          playerName: newPlayerName,
                        });
                        setNewPlayerName("");
                      },
                      "Player added",
                      "PLAYER_ADDED",
                      { playerName: newPlayerName }
                    )
                  }
                >
                  Add Player
                </button>
              </div>
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameSplitTeams({ token, currentTheme }),
                    "Teams created",
                    "TEAMS_SPLIT"
                  )
                }
                disabled={players.length < 2}
              >
                Split Teams
              </button>
            </>
          ) : null}
          {(isCrowdMode || teamsReady) ? (
            <>
              <div className="wml-current-prompt">
                <div className="wml-prompt-label">Selected question</div>
                {selectedQuestion ? (
                  <>
                    <p className="wml-prompt-text">{selectedQuestion.question}</p>
                    <ul className="wml-options-list">
                      {selectedOptions.map((opt, idx) => (
                        <li key={`opt-${idx}`}>
                          {idx + 1}. {opt.text} ({Number(opt.point) || 10})
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="wml-prompt-empty">Select a question from the table.</p>
                )}
              </div>
              {selectedQuestion ? (
                <div className="qu-form-group">
                  <label>Select option</label>
                  <select
                    value={selectedOptionNo}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedOptionNo(v);
                      if (!v) return;
                      run(
                        () =>
                          gameUpdateState({
                            token,
                            currentTheme,
                            patch: { selectedOptionNo: Number(v) },
                          }),
                        null,
                        null
                      );
                    }}
                  >
                    <option value="" disabled>
                      Select option
                    </option>
                    {selectedOptions.map((opt, idx) => (
                      <option key={`${idx}-${opt.text}`} value={idx + 1}>
                        {idx + 1}. {opt.text}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (isGameMode || isCrowdMode) ? (
                <p className="wml-prompt-empty">Select a question first to enable option selection.</p>
              ) : null}
            </>
          ) : null}
          {(isCrowdMode || teamsReady) ? (
            <div className="wml-action-row">
              {(selectedQuestion && (isCrowdMode || hasSelectedPlayer)) ? (
                <button
                  type="button"
                  className="btn outline"
                  onClick={() =>
                    run(
                      () => gameStartQuestion({ token, currentTheme, questionId: selectedQuestion.id }),
                      "Question released",
                      "QUESTION_RELEASED",
                      { questionId: Number(selectedQuestion.id) }
                    )
                  }
                >
                  Release Question
                </button>
              ) : null}
              {isGameMode && questionReleased ? (
                <button
                  type="button"
                  className="btn outline"
                  onClick={() => {
                    if (!hasValidSelectedOption) {
                      Swal.fire("Info", "Select an option before submitting answer.", "info");
                      return;
                    }
                    run(
                      () =>
                        gameSubmitAnswer({
                          token,
                          currentTheme,
                          questionId: selectedQuestion?.id,
                          selectedPlayerId: isGameMode ? selectedPlayerId : null,
                          optionNo: Number(selectedOptionNo),
                        }),
                      "Answer submitted",
                      "ANSWER_SUBMITTED",
                      {
                        questionId: Number(selectedQuestion?.id),
                        selectedPlayerId: isGameMode ? Number(selectedPlayerId) : null,
                        optionNo: Number(selectedOptionNo),
                      }
                    );
                  }}
                >
                  Submit Answer
                </button>
              ) : null}
              {questionReleased ? (
                <button
                  type="button"
                  className="btn outline"
                  onClick={() =>
                    run(
                      () => gameRestartTimer({ token, currentTheme }),
                      "Timer restarted",
                      "TIMER_RESTARTED"
                    )
                  }
                >
                  Restart Timer
                </button>
              ) : null}
              {questionReleased ? (
                <button
                  type="button"
                  className="btn outline"
                  onClick={() => {
                    if (!hasValidSelectedOption) {
                      Swal.fire("Info", "Select an option before revealing answer.", "info");
                      return;
                    }
                    run(
                      () => gameRevealAnswer({ token, currentTheme }),
                      "Answer revealed",
                      "ANSWER_REVEALED"
                    );
                  }}
                >
                  Reveal Answer
                </button>
              ) : null}
            </div>
          ) : null}
          {isGameMode && teamsReady ? (
            <div className="wml-action-row">
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameStrike({ token, currentTheme, team: "A", action: "increment" }),
                    null,
                    "STRIKE_UPDATED",
                    { team: "A", action: "increment" }
                  )
                }
              >
                Strike A +
              </button>
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameStrike({ token, currentTheme, team: "A", action: "reset" }),
                    null,
                    "STRIKE_UPDATED",
                    { team: "A", action: "reset" }
                  )
                }
              >
                Strike A Reset
              </button>
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameStrike({ token, currentTheme, team: "B", action: "increment" }),
                    null,
                    "STRIKE_UPDATED",
                    { team: "B", action: "increment" }
                  )
                }
              >
                Strike B +
              </button>
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameStrike({ token, currentTheme, team: "B", action: "reset" }),
                    null,
                    "STRIKE_UPDATED",
                    { team: "B", action: "reset" }
                  )
                }
              >
                Strike B Reset
              </button>
            </div>
          ) : null}
          {isGameMode && teamsReady ? (
          <div className="wml-action-row">
            {discussionActive ? (
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameDiscussion({ token, currentTheme, start: false }),
                    "Discussion stopped",
                    "DISCUSSION_STOPPED"
                  )
                }
              >
                Stop Discussion
              </button>
            ) : (
              <button
                type="button"
                className="btn outline"
                onClick={() =>
                  run(
                    () => gameDiscussion({ token, currentTheme, start: true }),
                    "Discussion started",
                    "DISCUSSION_STARTED"
                  )
                }
              >
                Start 30s Discussion
              </button>
            )}
            <button
              type="button"
              className="btn outline"
              onClick={() =>
                leaderboardActive
                  ? run(
                      () =>
                        gameUpdateState({
                          token,
                          currentTheme,
                          patch: { leaderboardVisible: false },
                        }),
                      "Leaderboard hidden",
                      "LEADERBOARD_HIDDEN",
                      { leaderboardVisible: false }
                    )
                  : run(
                      () => gameLeaderboard({ token, currentTheme }),
                      "Leaderboard visible",
                      "LEADERBOARD_VISIBLE"
                    )
              }
            >
              {leaderboardActive ? "Hide Leaderboard" : "Show Leaderboard"}
            </button>
          </div>
          ) : null}
        </div>
        <div className="wml-live-right">
          {isGameMode ? (
            <>
              <div className="wml-phase-pill">
                Selected team: <strong>{selectedPlayer?.team || state?.current_turn_team || "-"}</strong>
              </div>
              <div className="wml-vote-meta">
                <span>Strike A: {state?.strike_a ?? 0}</span>
                <span>Strike B: {state?.strike_b ?? 0}</span>
                {timerLeft != null ? <span>{timerLeft}s</span> : null}
              </div>
              <div className="wml-roster-block">
                <div className="wml-roster-title">Players</div>
                <table className="wml-roster-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Team</th>
                      {!teamsReady ? <th>Delete</th> : null}
                      {teamsReady ? <th>Select</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((p) => (
                      <tr key={p.id}>
                        <td>{p.player_name}</td>
                        <td>{p.team || "-"}</td>
                        {!teamsReady ? (
                          <td>
                            <button
                              type="button"
                              className="btn outline"
                              onClick={() =>
                                run(
                                  () =>
                                    gameDeletePlayer({
                                      token,
                                      currentTheme,
                                      playerId: Number(p.id),
                                    }),
                                  "Player deleted",
                                  "PLAYER_DELETED",
                                  { playerId: Number(p.id) }
                                )
                              }
                            >
                              Delete
                            </button>
                          </td>
                        ) : null}
                        {teamsReady ? (
                          <td>
                            <button
                              type="button"
                              className="btn outline"
                              onClick={() =>
                                run(
                                  async () => {
                                    await gameUpdateState({
                                      token,
                                      currentTheme,
                                      patch: { selectedPlayerId: p.id },
                                    });
                                    setSelectedPlayerId(p.id);
                                  },
                                  null,
                                  "PLAYER_SELECTED",
                                  { playerId: p.id, team: p.team || null }
                                )
                              }
                            >
                              {Number(selectedPlayerId) === Number(p.id) ? "Selected" : "Select Player"}
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {teamsReady ? (
                <div className="wml-roster-block">
                  <div className="wml-roster-title">Leaderboard</div>
                  <table className="wml-roster-table">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((l) => (
                        <tr key={l.player_id}>
                          <td>{l.player_name}</td>
                          <td>{l.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
